const DEFAULT_ORDER_COPY = Object.freeze({
  "subject": "Commande produits d’entretien Market Spas",
  "unit": "unité",
  "greeting": "Bonjour,",
  "intro": "Je souhaite commander les produits d’entretien suivants :",
  "page": "Page consultée :",
  "name": "Nom :",
  "phone": "Téléphone :",
  "delivery_address": "Adresse de livraison (rue, numéro, code postal, ville, pays) :",
  "store": "Magasin souhaité :",
  "closing": "Merci de confirmer la disponibilité, le prix TTC et les modalités de retrait ou de livraison."
});

export function normalizeQuantity(value) {
  const parsed = Number.parseInt(String(value), 10);
  if (!Number.isFinite(parsed) || parsed <= 0) return 0;
  return Math.min(parsed, 99);
}

export function selectedOrderLines(lines = []) {
  return lines
    .map((line) => ({
      title: String(line.title || "").trim(),
      quantity: normalizeQuantity(line.quantity),
      price: String(line.price || "").trim(),
    }))
    .filter((line) => line.title && line.quantity > 0);
}

export function buildOrderEmail({
  recipient = "service@marketspas.com",
  pageUrl = "",
  lines = [],
  subject = DEFAULT_ORDER_COPY.subject,
  copy = {},
} = {}) {
  const text = { ...DEFAULT_ORDER_COPY, ...copy };
  const selected = selectedOrderLines(lines);
  if (!selected.length) return { canSend: false, subject, body: "", href: "" };

  const productLines = selected.map(({ title, quantity, price }) => {
    const unitPrice = price ? ` — ${price} / ${text.unit}` : "";
    return `- ${quantity} × ${title}${unitPrice}`;
  });
  const body = [
    text.greeting,
    "",
    text.intro,
    "",
    ...productLines,
    "",
    `${text.page} ${pageUrl}`,
    "",
    text.name,
    text.phone,
    text.delivery_address,
    text.store,
    "",
    text.closing,
  ].join("\n");
  return {
    canSend: true,
    subject,
    body,
    href: `mailto:${recipient}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`,
  };
}

function initMsGallery(root) {
  if (root.dataset.msGalleryReady) return;
  root.dataset.msGalleryReady = "true";
  const panels = [...root.querySelectorAll("[data-ms-gallery-panel]")];
  const thumbs = [...root.querySelectorAll("[data-ms-gallery-thumb]")];
  if (!panels.length) return;
  let active = 0;
  const show = (index) => {
    active = (index + panels.length) % panels.length;
    panels.forEach((panel, panelIndex) => { panel.hidden = panelIndex !== active; });
    thumbs.forEach((thumb, thumbIndex) => thumb.setAttribute("aria-current", String(thumbIndex === active)));
  };
  thumbs.forEach((thumb) => thumb.addEventListener("click", () => show(Number(thumb.dataset.index))));
  root.querySelector("[data-ms-gallery-prev]")?.addEventListener("click", () => show(active - 1));
  root.querySelector("[data-ms-gallery-next]")?.addEventListener("click", () => show(active + 1));
}

function initMsOrder(root) {
  if (root.dataset.msOrderReady) return;
  root.dataset.msOrderReady = "true";
  const dialog = root.querySelector("[data-ms-order-dialog]");
  const submit = root.querySelector("[data-ms-order-submit]");
  const error = root.querySelector("[data-ms-order-error]");
  if (!dialog || !submit || !error) return;
  let copy = {};
  try { copy = JSON.parse(root.querySelector("[data-ms-order-i18n]")?.textContent || "{}"); } catch (_) { /* Keep the existing French fallback if locale data is unavailable. */ }
  // Écran tactile : pas de focus sur le champ de quantité. iOS zoome sur un champ de moins de 16 px qui prend le focus,
  // et la fenêtre s'ouvrait agrandie, coupée à gauche, clavier sorti (capture du client, 02/10/2026).
  // showModal() place déjà le focus sur le bouton de fermeture, premier élément de la fenêtre.
  const tactile = typeof window.matchMedia === "function" && window.matchMedia("(hover: none) and (pointer: coarse)").matches;
  const open = () => {
    if (typeof dialog.showModal === "function") dialog.showModal();
    else dialog.setAttribute("open", "");
    if (!tactile) dialog.querySelector("[data-ms-order-quantity]")?.focus();
  };
  document.querySelectorAll("[data-ms-order-open]").forEach((button) => {
    if (button.dataset.msOrderBound) return;
    button.dataset.msOrderBound = "true";
    button.addEventListener("click", open);
  });
  root.querySelector("[data-ms-order-close]")?.addEventListener("click", () => dialog.close());
  dialog.addEventListener("click", (event) => {
    const rect = dialog.getBoundingClientRect();
    const inside = event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom;
    if (!inside) dialog.close();
  });
  root.querySelectorAll("[data-ms-order-quantity]").forEach((input) => input.addEventListener("input", () => { error.hidden = true; }));
  submit.addEventListener("click", (event) => {
    const lines = [...root.querySelectorAll("[data-ms-order-row]")].map((row) => ({
      title: row.dataset.title,
      price: row.dataset.price,
      quantity: row.querySelector("[data-ms-order-quantity]")?.value,
    }));
    const result = buildOrderEmail({
      recipient: root.dataset.recipient,
      subject: root.dataset.subject,
      pageUrl: root.dataset.pageUrl || window.location.href,
      lines,
      copy,
    });
    if (!result.canSend) {
      event.preventDefault();
      error.hidden = false;
      error.focus?.();
      return;
    }
    submit.href = result.href;
  });
}

function initMsEntretienCommerce(scope = document) {
  scope.querySelectorAll("[data-ms-gallery]").forEach(initMsGallery);
  scope.querySelectorAll("[data-ms-order-section]").forEach(initMsOrder);
}

if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", () => initMsEntretienCommerce());
else initMsEntretienCommerce();
document.addEventListener("shopify:section:load", (event) => initMsEntretienCommerce(event.target));
